import { supabase } from '@/integrations/supabase/client';

export interface PostAuthRouteResolution {
  onboardingCompleted: boolean;
  target: string;
}

const AUTH_ROUTE_RETRY_COUNT = 6;
const AUTH_ROUTE_RETRY_DELAY_MS = 250;
const inflightRouteResolutions = new Map<string, Promise<PostAuthRouteResolution>>();

const buildOnboardingPath = (inviteCode = '') => {
  const trimmedInviteCode = inviteCode.trim();
  if (!trimmedInviteCode) return '/onboarding';

  const params = new URLSearchParams({ invite: trimmedInviteCode });
  return `/onboarding?${params.toString()}`;
};

const isDuplicatePreferencesError = (error: { code?: string; message?: string } | null) => {
  if (!error) return false;
  return error.code === '23505' || /duplicate key/i.test(error.message ?? '');
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitForSessionUser = async (userId: string) => {
  for (let attempt = 0; attempt < AUTH_ROUTE_RETRY_COUNT; attempt += 1) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.user?.id === userId) {
      return;
    }

    if (attempt < AUTH_ROUTE_RETRY_COUNT - 1) {
      await delay(AUTH_ROUTE_RETRY_DELAY_MS);
    }
  }
};

const readUserPreferences = async (userId: string) => {
  let lastError: { code?: string; message?: string } | null = null;

  for (let attempt = 0; attempt < AUTH_ROUTE_RETRY_COUNT; attempt += 1) {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('onboarding_completed')
      .eq('user_id', userId)
      .maybeSingle();

    if (!error) {
      return data;
    }

    lastError = error;

    if (attempt < AUTH_ROUTE_RETRY_COUNT - 1) {
      await delay(AUTH_ROUTE_RETRY_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError;
};

const ensureUserPreferences = async (userId: string) => {
  let lastError: { code?: string; message?: string } | null = null;

  for (let attempt = 0; attempt < AUTH_ROUTE_RETRY_COUNT; attempt += 1) {
    const { error } = await supabase.from('user_preferences').insert({ user_id: userId });

    if (!error || isDuplicatePreferencesError(error)) {
      return;
    }

    lastError = error;

    if (attempt < AUTH_ROUTE_RETRY_COUNT - 1) {
      await delay(AUTH_ROUTE_RETRY_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError;
};

const resolvePostAuthRouteInternal = async (
  userId: string,
  inviteCode = ''
): Promise<PostAuthRouteResolution> => {
  await waitForSessionUser(userId);

  let data: { onboarding_completed: boolean } | null = null;

  try {
    data = await readUserPreferences(userId);
  } catch {
    // RLS may block the query if the session hasn't fully propagated to
    // PostgREST yet.  For new users this is expected — fall through to the
    // onboarding path which is always the correct destination.
  }

  if (!data) {
    try {
      await ensureUserPreferences(userId);
    } catch {
      // INSERT may also be blocked by RLS.  That's OK — Home.tsx has a
      // fallback that will create the row when the session is ready.
    }

    // Even if the insert succeeded, we know onboarding hasn't been completed
    // because the row either didn't exist or we just created a fresh one.
    return {
      onboardingCompleted: false,
      target: buildOnboardingPath(inviteCode),
    };
  }

  if (!data || !data.onboarding_completed) {
    return {
      onboardingCompleted: false,
      target: buildOnboardingPath(inviteCode),
    };
  }

  return {
    onboardingCompleted: true,
    target: '/',
  };
};

export const resolvePostAuthRoute = async (
  userId: string,
  inviteCode = ''
): Promise<PostAuthRouteResolution> => {
  const trimmedInviteCode = inviteCode.trim();
  const cacheKey = `${userId}:${trimmedInviteCode}`;
  const pendingResolution = inflightRouteResolutions.get(cacheKey);

  if (pendingResolution) {
    return pendingResolution;
  }

  const nextResolution = resolvePostAuthRouteInternal(userId, trimmedInviteCode).finally(() => {
    inflightRouteResolutions.delete(cacheKey);
  });

  inflightRouteResolutions.set(cacheKey, nextResolution);
  return nextResolution;
};